// =============================================================================
// features/sales/campaigns/CampaignsList.tsx — Corpo da lista de campanhas:
// nome, público, responsável, status, progresso, próxima janela e alertas
// (doc 26 §6.5). Tabela no desktop, cards no mobile — MESMA definição de
// colunas (ResponsiveTable, DS §9.7 + doc 24 §6), sem duplicar a lógica de
// apresentação por breakpoint (F35-S07 DoD: "Mobile em cards; alvos ≥ 44px").
//
// "Público", "próxima janela" e "alertas" ficam "Indisponível" — a API de
// campanhas (F35-S02) não expõe esses agregados ainda (ver header de
// hooks/sales/useCampaigns.ts). Nunca calculados no cliente.
// =============================================================================

import type { Campaign } from '@elemento/shared-schemas';
import * as React from 'react';

import { Badge } from '../../../components/ui/Badge';
import type { ResponsiveTableColumn } from '../../../components/ui/ResponsiveTable';
import { ResponsiveTable } from '../../../components/ui/ResponsiveTable';

import { CampaignsPagination } from './CampaignsPagination';
import { CampaignsEmptyState, CampaignsErrorState } from './CampaignsStates';
import { CampaignProgress, CampaignStatusBadge } from './components';
import { formatDateOrgTz } from './timezone';

export interface CampaignsListProps {
  campaigns: Campaign[];
  isLoading: boolean;
  isError: boolean;
  hasFilters: boolean;
  currentUserId: string;
  canCreate: boolean;
  onRetry: () => void;
  onClearFilters: () => void;
  onCreate: () => void;
  offset: number;
  limit: number;
  total: number;
  onOffsetChange: (offset: number) => void;
}

function ownerLabel(ownerUserId: string, currentUserId: string): string {
  if (ownerUserId === currentUserId) return 'Você';
  // Sem um diretório de usuários seguro para este papel (ver nota em
  // CampaignsFilters.tsx sobre agent_id vs users.id), mostramos um rótulo
  // honesto em vez de arriscar um nome errado.
  return `Outro gestor (${ownerUserId.slice(0, 8)}…)`;
}

const COLUMNS = (currentUserId: string): ResponsiveTableColumn<Campaign>[] => [
  {
    key: 'name',
    header: 'Nome',
    primary: true,
    widthClassName: 'w-[240px]',
    cell: (c) => (
      <div className="min-w-0">
        <p className="font-sans font-semibold text-sm text-ink truncate" style={{ maxWidth: 220 }}>
          {c.name}
        </p>
        {c.description && (
          <p className="font-sans text-xs text-ink-4 truncate" style={{ maxWidth: 220 }}>
            {c.description}
          </p>
        )}
      </div>
    ),
  },
  {
    key: 'audience',
    header: 'Público',
    align: 'right',
    widthClassName: 'w-[100px]',
    cell: () => <span className="font-mono text-sm text-ink-4">Indisponível</span>,
  },
  {
    key: 'owner',
    header: 'Responsável',
    hideBelow: 'md',
    widthClassName: 'w-[160px]',
    cell: (c) => (
      <span className="font-sans text-sm text-ink-2">
        {ownerLabel(c.ownerUserId, currentUserId)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    widthClassName: 'w-[120px]',
    cell: (c) => <CampaignStatusBadge status={c.status} />,
  },
  {
    key: 'progress',
    header: 'Progresso',
    hideBelow: 'lg',
    widthClassName: 'w-[140px]',
    cell: () => <CampaignProgress percent={null} />,
  },
  {
    key: 'nextWindow',
    header: 'Próxima janela',
    hideBelow: 'xl',
    widthClassName: 'w-[140px]',
    cell: () => <span className="font-sans text-xs text-ink-4">Indisponível</span>,
  },
  {
    key: 'alerts',
    header: 'Alertas',
    hideBelow: 'lg',
    widthClassName: 'w-[120px]',
    cell: () => (
      <Badge variant="neutral" className="opacity-70">
        Sem dado
      </Badge>
    ),
  },
  {
    key: 'createdAt',
    header: 'Criada',
    hideBelow: 'xl',
    align: 'right',
    widthClassName: 'w-[110px]',
    cell: (c) => (
      <span className="font-sans text-xs text-ink-4">{formatDateOrgTz(c.createdAt)}</span>
    ),
  },
];

export function CampaignsList({
  campaigns,
  isLoading,
  isError,
  hasFilters,
  currentUserId,
  canCreate,
  onRetry,
  onClearFilters,
  onCreate,
  offset,
  limit,
  total,
  onOffsetChange,
}: CampaignsListProps): React.JSX.Element {
  const columns = React.useMemo(() => COLUMNS(currentUserId), [currentUserId]);

  if (isError) {
    return (
      <div
        className="rounded-md border border-border overflow-hidden"
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
      >
        <CampaignsErrorState onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-border overflow-hidden"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
    >
      <ResponsiveTable
        columns={columns}
        data={campaigns}
        getRowKey={(c) => c.id}
        isLoading={isLoading}
        emptyState={
          <CampaignsEmptyState
            filtered={hasFilters}
            onClearFilters={onClearFilters}
            onCreate={onCreate}
            canCreate={canCreate}
          />
        }
        aria-label="Lista de campanhas"
      />

      {!isLoading && campaigns.length > 0 && (
        <CampaignsPagination
          offset={offset}
          limit={limit}
          total={total}
          onOffsetChange={onOffsetChange}
        />
      )}
    </div>
  );
}
